<template>
	<sg-modal
		v-model="isOpen"
		:title="$t('user-invite.title')"
		:backdrop-dismiss="false"
		@did-dismiss="closeModal"
	>
		<div class="ion-padding">
			<!-- Mode selector -->
			<ion-segment v-model="mode" class="mb-4">
				<ion-segment-button value="email">
					<ion-label>{{ $t("user-invite.mode-email") }}</ion-label>
				</ion-segment-button>
				<ion-segment-button value="managed">
					<ion-label>{{ $t("user-invite.mode-managed") }}</ion-label>
				</ion-segment-button>
			</ion-segment>

			<p class="mb-4 text-sm text-gray-500">
				<template v-if="mode === 'email'">
					{{
						$t("user-invite.modal.description", {
							count: MAX_BULK_INVITES,
						})
					}}
					<br />
					<br />
					{{ $t("user-invite.modal.admin-note") }}
				</template>
				<template v-else>
					{{ $t("user-invite.modal.managed-description") }}
					<br />
					<br />
					{{ $t("user-invite.modal.managed-admin-note") }}
				</template>
			</p>
			<!-- Card-based form for invites -->
			<div class="mb-4 space-y-4">
				<!-- Invite cards -->
				<div
					v-for="(invite, index) in invites"
					:key="index"
					class="rounded-lg border border-gray-200 bg-white p-4 shadow-sm"
				>
					<div class="flex items-start justify-between">
						<div class="flex-1">
							<!-- Email mode: Email full width on top -->
							<div v-if="mode === 'email'" class="mb-4 w-full">
								<label
									class="mb-2 block text-sm font-medium text-gray-700"
								>
									{{ $t("user-invite.email") }}
									<span class="text-red-500">*</span>
								</label>
								<div class="flex items-center gap-2">
									<sg-input
										v-model="invite.email"
										type="email"
										:placeholder="
											$t('user-invite.email-placeholder')
										"
										fill="outline"
										:error="
											getUsernameError(index, 'email')
										"
										class="flex-1"
										@ion-blur="validateOnBlur"
									/>
									<username-availability-status-icon
										:status="
											getAvailabilityStatus(invite.email)
										"
									/>
								</div>
							</div>

							<!-- Name and Role row -->
							<div class="flex gap-3">
								<div
									:class="
										mode === 'email' ? 'flex-1' : 'w-full'
									"
								>
									<label
										class="mb-2 block text-sm font-medium text-gray-700"
									>
										{{ $t("user-invite.name") }}
									</label>
									<sg-input
										v-model="invite.name"
										type="text"
										:placeholder="
											$t('user-invite.name-placeholder')
										"
										fill="outline"
									/>
								</div>
								<div
									v-if="mode === 'email'"
									class="w-32 min-w-0"
								>
									<label
										class="mb-2 block text-sm font-medium text-gray-700"
									>
										{{ $t("user-invite.role") }}
									</label>
									<sg-select
										v-model="invite.role"
										:placeholder="
											$t('user-invite.select-role')
										"
										fill="outline"
									>
										<ion-select-option value="member">
											{{ $t("user-invite.member") }}
										</ion-select-option>
										<ion-select-option
											v-if="canInviteAdmins"
											value="admin"
										>
											{{ $t("user-invite.admin") }}
										</ion-select-option>
									</sg-select>
								</div>
							</div>

							<!-- Managed mode: Username below name and role -->
							<div v-if="mode === 'managed'" class="mt-4 w-full">
								<label
									class="mb-2 block text-sm font-medium text-gray-700"
								>
									{{ $t("user-invite.username") }}
									<span class="text-red-500">*</span>
								</label>
								<div class="flex items-center gap-2">
									<sg-input
										v-model="invite.userName"
										type="text"
										:placeholder="
											$t(
												'user-invite.username-placeholder',
											)
										"
										fill="outline"
										:error="
											getUsernameError(index, 'userName')
										"
										class="flex-1"
										@ion-blur="validateOnBlur"
									/>
									<username-availability-status-icon
										:status="
											getAvailabilityStatus(
												invite.userName,
											)
										"
									/>
								</div>
							</div>
						</div>

						<!-- Delete button -->
						<div
							class="ml-3 flex flex-none items-start justify-center"
						>
							<ion-button
								v-if="invites.length > 1"
								fill="clear"
								size="small"
								color="danger"
								@click="removeInvite(index)"
							>
								<ion-icon :icon="trashOutline" />
							</ion-button>
						</div>
					</div>
				</div>
			</div>

			<!-- Add another user button -->
			<div class="mb-6 flex justify-center">
				<ion-button
					fill="clear"
					size="small"
					:disabled="!canAddMoreInvites"
					@click="addInvite"
				>
					<ion-icon slot="start" :icon="addOutline" />
					{{ $t("user-invite.add-another-user") }}
				</ion-button>
			</div>
		</div>

		<template #footer>
			<div class="flex justify-end gap-3">
				<sg-button :on-click="closeModal">
					{{ $t("user-invite.cancel") }}
				</sg-button>
				<sg-button
					:disabled="!isFormValid || isLoading"
					:loading="isLoading"
					:on-click="confirmInvite"
				>
					{{
						mode === "managed"
							? $t("user-invite.create-invites")
							: $t("user-invite.send-invitation")
					}}
				</sg-button>
			</div>
		</template>
	</sg-modal>
</template>

<script setup lang="ts">
	import { addOutline, trashOutline } from "ionicons/icons";
	import {
		IonButton,
		IonIcon,
		IonLabel,
		IonSegment,
		IonSegmentButton,
		IonSelectOption,
		alertController,
	} from "@ionic/vue";
	import SgInput from "@/components/ui/SgInput.vue";
	import SgSelect from "@/components/ui/SgSelect.vue";
	import SgModal from "@/components/ui/modals/SgModal.vue";
	import SgButton from "@/components/ui/SgButton.vue";
	import UsernameAvailabilityStatusIcon from "@/components/feature/auth/invite/UsernameAvailabilityStatusIcon.vue";
	import { ref, computed, watch } from "vue";
	import { useI18n } from "vue-i18n";
	import { useValidation } from "@/composables/useValidation";
	import { useUsernameAvailability } from "@/composables/auth/useUsernameAvailability";
	import { makeInviteFormSchema } from "@/validation/invite.schema";
	import { useCreateBulkInvites } from "seedgreen-shared/queries/invite";
	import { useMyUser } from "seedgreen-shared/queries/user";
	import type {
		CreateInviteDTO,
		BulkInviteResultDTO,
	} from "seedgreen-shared/dtos/InviteDTO";
	import { getInviteErrorTranslationKey } from "seedgreen-shared/dtos/InviteDTO";
	import * as Sentry from "@sentry/vue";

	const { t } = useI18n();
	const { createBulkInvites, isLoading } = useCreateBulkInvites();
	const { myUser } = useMyUser();

	const emit = defineEmits<{
		success: [mode: "email" | "managed", results: BulkInviteResultDTO];
	}>();

	interface InviteFormRow {
		email: string;
		userName: string;
		name: string;
		role: "member" | "admin";
	}

	const isOpen = ref<boolean>(false);
	const mode = ref<"email" | "managed">("email");

	// Maximum number of invites that can be processed at once (varies by mode)
	const MAX_BULK_INVITES = computed(() =>
		mode.value === "email" ? 100 : 25,
	);
	const invites = ref<InviteFormRow[]>([
		{
			email: "",
			userName: "",
			name: "",
			role: "member",
		},
	]);

	// Validation using Zod schema
	const inviteFormSchema = makeInviteFormSchema(t, {
		getMax: () => MAX_BULK_INVITES.value,
	});

	const { validate, validationErrors, clearErrors } =
		useValidation(inviteFormSchema);

	// Username availability checking
	// Extract usernames based on mode for the composable
	const usernamesToCheck = computed(() =>
		invites.value.map((invite) =>
			mode.value === "email" ? invite.email : invite.userName,
		),
	);

	const { getAvailabilityStatus, allUsernamesAvailable } =
		useUsernameAvailability(usernamesToCheck);

	/**
	 * Gets the error message for a username field (email or userName)
	 */
	const getUsernameError = (
		index: number,
		field: "email" | "userName",
	): string | undefined => {
		const key = `invites.${index}.${field}`;
		const validationError = validationErrors.value[key];
		if (validationError && typeof validationError === "string") {
			return validationError;
		}

		const username = invites.value[index][field];
		if (getAvailabilityStatus(username) === "taken") {
			return field === "email"
				? t("user-invite.email-already-in-use")
				: t("user-invite.username-already-taken");
		}

		return undefined;
	};

	/**
	 * Validates the form on blur.
	 * Provides feedback as users fill out fields.
	 */
	const validateOnBlur = () => {
		validate({ mode: mode.value, invites: invites.value });
	};

	/**
	 * All invites must be valid to submit.
	 */
	const isFormValid = computed(() => {
		// Check if user has a valid customer ID
		if (!myUser.value?.customerId) {
			return false;
		}

		// Check if all invites pass Zod validation
		const validationPassed = inviteFormSchema.safeParse({
			mode: mode.value,
			invites: invites.value,
		}).success;

		if (!validationPassed) {
			return false;
		}

		// For managed mode, ensure all usernames are available
		// For email mode, ensure all emails (which become usernames) are available
		return allUsernamesAvailable.value;
	});

	// Validate when mode changes (to show/clear appropriate errors)
	watch(mode, () => {
		validate({ mode: mode.value, invites: invites.value });
	});

	const canAddMoreInvites = computed(() => {
		return invites.value.length < MAX_BULK_INVITES.value;
	});

	const canInviteAdmins = computed(() => {
		// Only internal users and admin users can invite other admins
		return (
			myUser.value?.internalUser === true || myUser.value?.admin === true
		);
	});

	const addInvite = () => {
		if (invites.value.length >= MAX_BULK_INVITES.value) {
			return; // Prevent adding more than the maximum
		}

		invites.value.push({
			email: "",
			userName: "",
			name: "",
			role: "member",
		});
		clearErrors();
	};

	const removeInvite = (index: number) => {
		if (invites.value.length > 1) {
			invites.value.splice(index, 1);
			clearErrors();
		}
	};

	const resetForm = () => {
		invites.value = [
			{
				email: "",
				userName: "",
				name: "",
				role: "member",
			},
		];
		clearErrors();
	};

	const closeModal = () => {
		resetForm();
		isOpen.value = false;
	};

	const openModal = () => {
		isOpen.value = true;
	};

	const confirmInvite = async () => {
		const result = validate({ mode: mode.value, invites: invites.value });
		if (!result.success) return;

		const alert = await alertController.create({
			header: t("user-invite.confirm-header"),
			message: t("user-invite.confirm-message-bulk", {
				count: invites.value.length,
			}),
			cssClass: "alert-confirm",
			buttons: [
				{
					text: t("user-invite.confirm-button-invite"),
					cssClass: "alert-confirm-btn",
					handler: () => {
						submitInvitations(invites.value);
					},
				},
				{
					text: t("user-invite.confirm-button-cancel"),
					role: "cancel",
					cssClass: "alert-cancel-btn",
				},
			],
		});

		await alert.present();
	};

	const submitInvitations = async (validInvites: InviteFormRow[]) => {
		try {
			const inviteData: CreateInviteDTO[] = validInvites.map(
				(invite) => ({
					email: invite.email || undefined,
					userName: invite.userName || invite.email,
					name: invite.name.trim() || undefined,
					admin:
						mode.value === "email" // managed accounts are always members
							? invite.role === "admin"
							: false,
					customerId: myUser.value?.customerId as number,
				}),
			);

			const results: BulkInviteResultDTO =
				await createBulkInvites(inviteData);

			if (mode.value === "managed") {
				// For managed mode, emit success and let parent handle showing links
				emit("success", mode.value, results);
				closeModal();
			} else {
				// For email mode, show alert as before
				let header: string;
				let message: string;

				if (results.successCount === 0) {
					// All invites failed
					header = t("user-invite.submit-failure-header");
					message = t("user-invite.submit-failure-message-all");
					if (results.failed && results.failed.length > 0) {
						message += "<br>";
						message += results.failed
							.map(
								(failure) =>
									`• ${failure.userName}: ${t(getInviteErrorTranslationKey(failure.errorCode))}`,
							)
							.join("<br>");
					}
				} else {
					// At least some invites succeeded
					header = t("user-invite.submit-success-header");
					message = t("user-invite.submit-success-message-bulk", {
						count: results.successCount,
					});

					// Add failure details if there are any
					if (results.failed && results.failed.length > 0) {
						message += "<br><br>";
						message += t("user-invite.submit-success-failures", {
							count: results.failed.length,
						});
						message += ":<br>";
						message += results.failed
							.map(
								(failure) =>
									`• ${failure.userName}: ${t(getInviteErrorTranslationKey(failure.errorCode))}`,
							)
							.join("<br>");
					}
				}

				const successAlert = await alertController.create({
					header,
					message,
					buttons: ["OK"],
				});

				await successAlert.present();
				closeModal();
			}
		} catch (error) {
			console.error("Error creating invites:", error);

			// Capture error with Sentry including context about the bulk operation
			Sentry.captureException(error, {
				tags: { feature: "bulk_invite" },
				extra: {
					inviteCount: validInvites.length,
					inviteEmails: validInvites.map((i) => i.email),
					customerId: myUser.value?.customerId,
					hasAdminInvites: validInvites.some(
						(i) => i.role === "admin",
					),
				},
			});

			const errorAlert = await alertController.create({
				header: t("user-invite.submit-error-header"),
				message: t("user-invite.submit-error-message"),
				buttons: ["OK"],
			});

			await errorAlert.present();
		}
	};

	defineExpose({
		openModal,
		closeModal,
	});
</script>
